import { FileFormats } from '@/applications/CspPortal/Campaigns/types';
import {
  globalDeleteService,
  globalGetService,
  globalPostService,
} from '@/utils/globalApiServices';
import { Attachment, AttachmentFolder } from './types';

export const uploadAttachment = async (params: {
  folder: AttachmentFolder;
  file: File;
}) => {
  const data = new FormData();
  data.append('folderType', params.folder);
  data.append('file', params.file);
  const response = await globalPostService('csp/attachments', data);
  return response.ok ? (response.data as Attachment) : undefined;
};

export const uploadByUrl = async (
  params: {
    folderType:
      | AttachmentFolder.BrandAssetLogo
      | AttachmentFolder.BrandAssetBanner;
    url: string;
  },
  configs?: Record<string, unknown>
) => {
  const response = await globalPostService(
    `csp/attachments/url?folderType=${
      params.folderType
    }&url=${encodeURIComponent(params.url)}`,
    undefined,
    configs
  );
  if (response.ok) {
    const attachment = response.data as Attachment;
    return {
      ...attachment,
      fileName: attachment.fileName.split('.').slice(0, -1).join('.'),
    };
  }
  return {
    error:
      response.data?.errorMessages?.[0] || response.message || 'Unknown error',
  };
};

export const downloadAttachment = async (props: { uuid: string }) => {
  const { uuid } = props;
  const response = await globalGetService(
    `csp/attachments/download?attachmentUuid=${encodeURIComponent(uuid)}`,
    {},
    'arraybuffer'
  );
  const attachment = response?.ok ? (response.data as ArrayBuffer) : undefined;
  if (attachment) {
    const contentDisposition = response.headers['content-disposition'];
    let filename = uuid; // Fallback to uuid
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch?.[1]) {
        // Remove file extension from the filename
        filename = filenameMatch[1].split('.').slice(0, -1).join('.');
      }
    }

    return new File([attachment], filename, {
      type: response.headers['content-type'],
    });
  }
  return undefined;
};

export const deleteAttachment = async (attachmentUuid: string) => {
  const encodedAttachmentUuid = encodeURIComponent(attachmentUuid);
  const response = await globalDeleteService(
    `csp/attachments/delete?attachmentUuid=${encodedAttachmentUuid}`
  );
  return !!response?.ok;
};

export const getAttachments = async (brandUid: string, folder: string) => {
  const response = await globalGetService(
    `csp/attachments/brand/${brandUid}?folder=${encodeURIComponent(folder)}`
  );

  return (response?.ok ? response.data : []) as Attachment[];
};

export const getAttachmentsForCampaign = async (
  campaignUid: string,
  folder: AttachmentFolder
) => {
  const response = await globalGetService(
    `csp/attachments/campaign/${campaignUid}?folder=${encodeURIComponent(
      folder
    )}`
  );

  return (response?.ok ? response.data : []) as Attachment[];
};

export const uploadAttachmentForCampaign = async (
  campaignUid: string,
  folder: AttachmentFolder,
  file: File
) => {
  const data = new FormData();
  data.append('file', file);
  const response = await globalPostService(
    `csp/attachments/campaign/${campaignUid}/folder/${folder}`,
    data
  );
  return response?.ok ? response.data : undefined;
};

export const getSupportedFileFormats = async (folder: string) => {
  const response = await globalGetService(
    `/csp/attachments/campaignFolder/${folder}/supportedFileFormats`
  );
  return response?.ok ? (response.data as FileFormats) : undefined;
};
